import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { X, ArrowUpRight, Handshake, ReceiptText, Ruler, PlugZap } from "lucide-react";
import { brand } from "../mock/content";
import { t } from "../lib/ui";

const ConfigModalContext = createContext({ openConfigModal: () => {}, closeConfigModal: () => {} });
export const useConfigModal = () => useContext(ConfigModalContext);

// Partner attivo (solo mentre si è sulla sua pagina): viene passato al configuratore.
const CONFIG_HOST = "https://configuratore.torri.solar";
let currentPartner = null;
const partnerListeners = new Set();
export function setConfigPartner(p) {
  currentPartner = p;
  partnerListeners.forEach((fn) => fn(p));
}
export function withPartnerParams(url) {
  if (!currentPartner || typeof url !== "string" || !url.startsWith(CONFIG_HOST)) return url;
  const u = new URL(url);
  u.searchParams.set("partner", currentPartner.slug);
  if (currentPartner.id) u.searchParams.set("partner_id", currentPartner.id);
  u.searchParams.set("source_page", `/${currentPartner.slug}`);
  return u.toString();
}
const useConfigPartner = () => {
  const [p, setP] = useState(currentPartner);
  useEffect(() => { partnerListeners.add(setP); return () => partnerListeners.delete(setP); }, []);
  return p;
};

const OPTIONS = [
  { id: "chiavi", icon: Handshake, href: "https://configuratore.torri.solar/configura" },
  { id: "bolletta", icon: ReceiptText, href: "https://configuratore.torri.solar/free/bolletta" },
  { id: "tetto", icon: Ruler, href: "https://configuratore.torri.solar/free/tetto" },
  { id: "colonnina", icon: PlugZap, href: "https://configuratore.torri.solar/free/colonnina", full: true },
];

function ConfigModal({ onClose }) {
  const [show, setShow] = useState(false);
  const partner = useConfigPartner();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShow(true));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="config-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity duration-300 ${show ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain bg-[#0a0a0a] border border-white/10 rounded-xl p-6 md:p-10 transition-all duration-300 ${show ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-[0.98]"}`}
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}
      >
        <button
          onClick={onClose}
          aria-label="Chiudi"
          data-testid="config-modal-close"
          className="absolute top-4 right-4 md:top-5 md:right-5 w-9 h-9 flex items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors"
        >
          <X size={18} />
        </button>

        <span className="text-[11px] md:text-[11px] uppercase tracking-[0.24em] font-bold" style={{ fontFamily: "Space Grotesk", color: brand.yellow }}>
          {t("cm_eyebrow")}
        </span>
        <h2 className="mt-3 font-black text-2xl md:text-4xl tracking-tight leading-none text-white" style={{ fontFamily: "Archivo" }}>
          {t("cm_title")}
        </h2>
        <p className="mt-3 text-white/55 text-sm md:text-base max-w-xl" style={{ fontFamily: "Inter" }}>
          {t("cm_sub")}
        </p>

        {partner && (
          <div className="mt-6 flex items-center gap-3 px-4 py-3 border rounded-lg" style={{ borderColor: `${brand.yellow}66`, background: `${brand.yellow}0f` }} data-testid="config-modal-partner">
            <Handshake size={18} style={{ color: brand.yellow }} className="shrink-0" />
            <span className="text-sm text-white/80" style={{ fontFamily: "Inter" }}>
              {t("cm_partner")} <strong className="text-white">{partner.name}</strong>
            </span>
          </div>
        )}

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <a
                key={o.id}
                href={withPartnerParams(o.href)}
                target="_blank"
                rel="noreferrer"
                data-testid={`config-option-${o.id}`}
                className="group relative flex flex-col p-5 md:p-6 bg-white/[0.03] border border-white/10 rounded-lg transition-all duration-200 hover:border-[color:var(--y)] hover:bg-white/[0.06] hover:-translate-y-1"
                style={{ "--y": brand.yellow }}
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-full mb-4 transition-colors" style={{ background: `${brand.yellow}1a`, color: brand.yellow }}>
                  <Icon size={22} strokeWidth={1.8} />
                </span>
                <span className="font-black text-lg md:text-xl tracking-tight text-white leading-tight" style={{ fontFamily: "Archivo" }}>
                  {t(`cm_${o.id}_t`)}
                </span>
                <span className="mt-2 text-white/55 text-sm leading-relaxed flex-1" style={{ fontFamily: "Inter" }}>
                  {t(`cm_${o.id}_d`)}
                </span>
                <ArrowUpRight size={20} className="mt-4 text-white/60 group-hover:text-[color:var(--y)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" style={{ "--y": brand.yellow }} />
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export const ConfigModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openConfigModal = useCallback(() => setOpen(true), []);
  const closeConfigModal = useCallback(() => setOpen(false), []);
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (a && currentPartner && a.href.startsWith(CONFIG_HOST)) a.href = withPartnerParams(a.href);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  return (
    <ConfigModalContext.Provider value={{ openConfigModal, closeConfigModal }}>
      {children}
      {open && <ConfigModal onClose={closeConfigModal} />}
    </ConfigModalContext.Provider>
  );
};
